import React, { useEffect, useState } from 'react';
import HoursService from '../../service/HoursService';
import { Spinner, Form, Modal, Accordion } from 'react-bootstrap';
import { Check, X, User, Clock, CheckCircle, XCircle, AlertCircle, ChevronLeft, ChevronRight, Search } from 'lucide-react';
import { getMinutosFromHora, minutosATexto } from '../../utils/hoursUtils';
import '../../styles/view/ApproveHours.css';

function getInitials(nombre = '', apellido = '') {
  return `${nombre.charAt(0)}${apellido.charAt(0)}`.toUpperCase() || '?';
}

export default function ApproveHours() {
  const [hours, setHours]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');

  const [search, setSearch]             = useState('');
  const [sortOrder, setSortOrder]       = useState('desc');
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [page, setPage]                 = useState(1);

  const [showModal, setShowModal]       = useState(false);
  const [selectedHour, setSelectedHour] = useState(null);
  const [actionType, setActionType]     = useState(null);
  const [comment, setComment]           = useState('');
  const [processing, setProcessing]     = useState(false);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      setError('');
      const data = await HoursService.getHours({ isAprobada: 0 });
      setHours(data);
    } catch (e) {
      console.error(e);
      setError('Error al cargar las horas pendientes.');
    } finally {
      setLoading(false);
    }
  };

  const openActionModal = (hour, type) => {
    setSelectedHour(hour);
    setActionType(type);
    setComment(type === 'approve' ? 'Aprobado' : '');
    setShowModal(true);
  };

  const handleAction = async () => {
    if (!selectedHour) return;
    if (actionType === 'reject' && !comment.trim()) return;
    setProcessing(true);
    try {
      await HoursService.approveHours(selectedHour.id, {
        aprobar: actionType === 'approve' ? 1 : 2,
        comentario: comment || (actionType === 'approve' ? 'Aprobado' : 'Rechazado')
      });
      setShowModal(false);
      setComment(''); setSelectedHour(null); setActionType(null);
      fetchData();
    } catch (e) {
      console.error(e);
      setError('Error al procesar la solicitud. ' + (e.response?.data?.message || ''));
    } finally {
      setProcessing(false);
    }
  };

  const filtered = hours.filter(h => {
    const fullName = `${h.usuario?.nombre ?? ''} ${h.usuario?.apellido ?? ''}`.toLowerCase();
    return fullName.includes(search.toLowerCase());
  });

  const sorted = [...filtered].sort((a, b) => {
    const da = new Date(a.fechaIngreso), db = new Date(b.fechaIngreso);
    return sortOrder === 'asc' ? da - db : db - da;
  });

  const totalPages  = Math.max(1, Math.ceil(sorted.length / itemsPerPage));
  const paginated   = sorted.slice((page - 1) * itemsPerPage, page * itemsPerPage);

  const groupedPending = paginated.reduce((acc, curr) => {
    const uid = curr.usuarioId;
    if (!acc[uid]) acc[uid] = { user: curr.usuario, hours: [] };
    acc[uid].hours.push(curr);
    return acc;
  }, {});

  const totalPendingMin = hours.reduce((acc, h) => acc + getMinutosFromHora(h), 0);
  const totalPending  = minutosATexto(totalPendingMin);
  const totalUsers    = Object.keys(groupedPending).length;

  if (loading) return (
    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
      <Spinner animation="border" style={{ color: 'var(--primary-color, #6366f1)' }} />
    </div>
  );

  return (
    <div className="ah-root">
      <div className="container-fluid p-3 p-md-4">

        {/* ── HEADER ── */}
        <div className="ah-header mb-4">
          <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center gap-3">
            <div>
              <h3>Aprobación de Horas</h3>
              <p className="mt-1">Revisa y gestiona las solicitudes de horas extra pendientes</p>
            </div>
            <div className="ah-header-badge">
              <Clock size={15} />
              <span>{hours.length} pendiente{hours.length !== 1 ? 's' : ''}</span>
            </div>
          </div>
        </div>

        {/* ── STAT CARDS ── */}
        <div className="row g-3 mb-4" style={{ marginTop: '1rem' }}>
          {[
            { label: 'Horas Pendientes',      value: totalPending, icon: <Clock size={20} color="var(--warning,#f59e0b)" />,   bg: 'rgba(245,158,11,.1)'  },
            { label: 'Usuarios con Solicitud', value: totalUsers,                    icon: <User size={20} color="var(--primary-color,#6366f1)" />, bg: 'rgba(99,102,241,.1)'  },
            { label: 'Total Solicitudes',      value: hours.length,                  icon: <CheckCircle size={20} color="var(--info,#3b82f6)" />,  bg: 'rgba(59,130,246,.1)'  },
          ].map((s, i) => (
            <div key={i} className="col-12 col-sm-4">
              <div className="ah-stat-card">
                <div className="icon-wrap" style={{ background: s.bg }}>{s.icon}</div>
                <div>
                  <div className="stat-value">{s.value}</div>
                  <div className="stat-label">{s.label}</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* ── ERROR ── */}
        {error && (
          <div className="ah-alert mb-4">
            <AlertCircle size={16} />
            <span>{error}</span>
            <button className="ah-alert-close" onClick={() => setError('')}><X size={14} /></button>
          </div>
        )}

        {/* ── FILTER BAR ── */}
        <div className="ah-filter-bar mb-4">
          <div className="row g-3 align-items-end">
            <div className="col-12 col-md-5">
              <label className="ah-filter-label">Buscar usuario</label>
              <div className="ah-search-wrap">
                <Search size={15} className="search-icon" />
                <input type="text" className="form-control"
                  placeholder="Nombre o apellido…"
                  value={search}
                  onChange={e => { setSearch(e.target.value); setPage(1); }} />
              </div>
            </div>
            <div className="col-12 col-sm-6 col-md-4">
              <label className="ah-filter-label">Ordenar por fecha</label>
              <select className="form-select" value={sortOrder}
                onChange={e => setSortOrder(e.target.value)}>
                <option value="desc">Más reciente</option>
                <option value="asc">Más antiguo</option>
              </select>
            </div>
            <div className="col-12 col-sm-6 col-md-3">
              <label className="ah-filter-label">Por página</label>
              <select className="form-select" value={itemsPerPage}
                onChange={e => { setItemsPerPage(Number(e.target.value)); setPage(1); }}>
                {[5, 10, 20, 50].map(n => <option key={n} value={n}>{n} / pág</option>)}
              </select>
            </div>
          </div>
        </div>

        {/* ── SECTION TITLE ── */}
        <div className="d-flex align-items-center gap-2 mb-3">
          <h5 className="mb-0 fw-bold" style={{ color: 'var(--text-primary)' }}>Pendientes de Aprobación</h5>
          {Object.keys(groupedPending).length > 0 && (
            <span className="ah-count-badge">{Object.keys(groupedPending).length} usuario{Object.keys(groupedPending).length !== 1 ? 's' : ''}</span>
          )}
        </div>

        {/* ── ACCORDION ── */}
        {Object.keys(groupedPending).length === 0 ? (
          <div className="ah-table-card">
            <div className="ah-empty">
              <CheckCircle size={48} color="var(--success,#10b981)" style={{ opacity: .25 }} />
              <p className="mt-3 mb-0">No hay horas pendientes de aprobación</p>
            </div>
          </div>
        ) : (
          <div className="ah-accordion-wrap">
            <Accordion defaultActiveKey="0">
              {Object.values(groupedPending).map((group, index) => (
                <Accordion.Item eventKey={String(index)} key={index} className="ah-accordion-item">
                  <Accordion.Header className="ah-accordion-header">
                    <div className="d-flex align-items-center gap-3 w-100 pe-2">
                      <div className="ah-user-avatar">
                        {getInitials(group.user?.nombre, group.user?.apellido)}
                      </div>
                      <div className="flex-grow-1">
                        <div className="fw-semibold" style={{ fontSize: '.9rem', color: 'var(--text-primary)' }}>
                          {group.user?.nombre} {group.user?.apellido}
                        </div>
                        <div style={{ fontSize: '.75rem', color: 'var(--text-secondary)' }}>
                          {group.user?.cedula}
                        </div>
                      </div>
                      <span className="ah-pending-badge">
                        {group.hours.length} solicitud{group.hours.length !== 1 ? 'es' : ''}
                      </span>
                    </div>
                  </Accordion.Header>

                  <Accordion.Body className="p-0">
                    <div className="table-responsive">
                      <table className="ah-table align-middle">
                        <thead>
                          <tr>
                            <th>Fecha</th>
                            <th>Horas</th>
                            <th className="d-none d-md-table-cell">Comentarios</th>
                            <th className="text-end">Acciones</th>
                          </tr>
                        </thead>
                        <tbody>
                          {group.hours.map(h => (
                            <tr key={h.id}>
                              <td>
                                <div className="d-flex flex-column">
                                  <span className="fw-semibold" style={{ fontSize: '.875rem' }}>
                                    {new Date(h.fechaIngreso).toLocaleDateString('es-ES', { day: '2-digit', month: 'short' })}
                                  </span>
                                  <span style={{ fontSize: '.73rem', color: 'var(--text-secondary)' }}>
                                    {new Date(h.fechaIngreso).toLocaleDateString('es-ES', { year: 'numeric' })}
                                  </span>
                                </div>
                              </td>
                              <td>
                                <span className="ah-hours-badge">{minutosATexto(getMinutosFromHora(h))}</span>
                              </td>
                              <td className="d-none d-md-table-cell" style={{ fontSize: '.82rem', color: 'var(--text-secondary)', maxWidth: '220px' }}>
                                {h.comentarios
                                  ? <span title={h.comentarios}>{h.comentarios.length > 40 ? `${h.comentarios.substring(0, 40)}…` : h.comentarios}</span>
                                  : <span style={{ opacity: .4 }}>—</span>}
                              </td>
                              <td className="text-end">
                                <div className="d-flex justify-content-end gap-2">
                                  <button className="ah-btn-action approve" onClick={() => openActionModal(h, 'approve')}>
                                    <Check size={13} />
                                    <span className="d-none d-sm-inline">Aprobar</span>
                                  </button>
                                  <button className="ah-btn-action reject" onClick={() => openActionModal(h, 'reject')}>
                                    <X size={13} />
                                    <span className="d-none d-sm-inline">Rechazar</span>
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </Accordion.Body>
                </Accordion.Item>
              ))}
            </Accordion>
          </div>
        )}

        {/* ── PAGINATION ── */}
        {totalPages > 1 && (
          <div className="ah-pagination d-flex justify-content-between align-items-center mt-3">
            <span>
              {sorted.length === 0
                ? 'Sin resultados'
                : `Mostrando ${(page - 1) * itemsPerPage + 1}–${Math.min(page * itemsPerPage, sorted.length)} de ${sorted.length}`}
            </span>
            <div className="d-flex align-items-center gap-2">
              <button className="btn-page" disabled={page === 1} onClick={() => setPage(p => p - 1)}>
                <ChevronLeft size={14} />
              </button>
              <span style={{ fontWeight: 600 }}>Pág. {page} / {totalPages}</span>
              <button className="btn-page" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── MODAL ── */}
      <Modal show={showModal} onHide={() => !processing && setShowModal(false)} centered className="ah-modal">
        <Modal.Header closeButton>
          <Modal.Title>
            <div className="d-flex align-items-center gap-2">
              {actionType === 'approve'
                ? <><CheckCircle size={18} /> Aprobar Horas</>
                : <><XCircle size={18} /> Rechazar Horas</>}
            </div>
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {/* Info card */}
          <div className="ah-modal-info-card mb-4">
            <div className="row g-3">
              <div className="col-12 col-sm-6">
                <div className="ah-info-item">
                  <span className="info-label">Usuario</span>
                  <span className="info-value">{selectedHour?.usuario?.nombre} {selectedHour?.usuario?.apellido}</span>
                </div>
              </div>
              <div className="col-6 col-sm-3">
                <div className="ah-info-item">
                  <span className="info-label">Horas</span>
                  <span className="info-value">{minutosATexto(getMinutosFromHora(selectedHour))}</span>
                </div>
              </div>
              <div className="col-6 col-sm-3">
                <div className="ah-info-item">
                  <span className="info-label">Fecha</span>
                  <span className="info-value">
                    {selectedHour?.fechaIngreso
                      ? new Date(selectedHour.fechaIngreso).toLocaleDateString('es-ES')
                      : '—'}
                  </span>
                </div>
              </div>
              {selectedHour?.comentarios && (
                <div className="col-12">
                  <div className="ah-info-item">
                    <span className="info-label">Comentario del usuario</span>
                    <span className="info-value" style={{ fontWeight: 400, fontSize: '.85rem' }}>
                      {selectedHour.comentarios}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Comment field */}
          <Form.Group>
            <Form.Label className="ah-modal-label">
              {actionType === 'reject' ? 'Razón del rechazo' : 'Comentario (Opcional)'}
              {actionType === 'reject' && <span style={{ color: 'var(--danger)' }}> *</span>}
            </Form.Label>
            <Form.Control
              as="textarea" rows={4}
              value={comment}
              onChange={e => setComment(e.target.value)}
              required={actionType === 'reject'}
              disabled={processing}
              placeholder={actionType === 'reject'
                ? 'Por favor, indique la razón del rechazo…'
                : 'Comentario opcional…'}
              className="ah-modal-textarea"
            />
            {actionType === 'reject' && !comment.trim() && (
              <div style={{ fontSize: '.78rem', color: 'var(--danger)', marginTop: '.35rem' }}>
                El comentario es obligatorio para rechazar.
              </div>
            )}
          </Form.Group>
        </Modal.Body>

        <Modal.Footer>
          <button className="ah-modal-btn cancel" onClick={() => setShowModal(false)} disabled={processing}>
            Cancelar
          </button>
          <button
            className={`ah-modal-btn ${actionType === 'approve' ? 'confirm-approve' : 'confirm-reject'}`}
            onClick={handleAction}
            disabled={processing || (actionType === 'reject' && !comment.trim())}
          >
            {processing ? (
              <><Spinner animation="border" size="sm" className="me-2" />Procesando…</>
            ) : actionType === 'approve' ? (
              <><Check size={15} /> Confirmar Aprobación</>
            ) : (
              <><X size={15} /> Confirmar Rechazo</>
            )}
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}